<template>
  <b-collapse class="evaluation-card" animation="slide" :open="isOpen" data-cy="params-data">
    <template #trigger="{ open }">
      <div class="card-header" role="button" :aria-expanded="open.toString()">
        <p class="card-header-title">
          Parámetros usados
        </p>
        <a class="card-header-icon">
          <b-icon :icon="open ? 'minus' : 'plus'" />
        </a>
      </div>
    </template>
    <div class="card-block p-0 pb-1">
      <!-- Segmento -->
      <div class="subcard">
        <b-field label="Segmento" horizontal>
          <b-select
            v-model="simulation.offerParams.segment"
            :disabled="readOnly"
            size="is-small"
            placeholder="Selecciona un segmento"
            expanded>
            <option value="SUBSIDY">Subsidio</option>
          </b-select>
        </b-field>

        <b-field label="Subsegmento" horizontal>
          <b-select
            v-model="simulation.offerParams.subSegment"
            :disabled="readOnly"
            size="is-small"
            placeholder="Selecciona un subsegmento"
            expanded>
            <option value="SUBSIDY_1_1">1.1</option>
            <option value="SUBSIDY_2_1">2.1</option>
            <option value="SUBSIDY_2_2B">2.2B</option>
            <option value="SUBSIDY_1_1B">1.1B</option>
            <option value="SUBSIDY_2_1">2.1B</option>
            <option value="SUBSIDY_1_1">1.1B</option>
            <option value="SUBSIDY_1_2">1.2B</option>
            <option value="SUBSIDY_1_2B">1.2B</option>
            <option value="SUBSIDY_2_1B">2.1B</option>
            <option value="SUBSIDY_2_2">2.2B</option>
          </b-select>
        </b-field>
      </div>

      <!-- Restricciones financieras -->
      <div class="subcard is-gray">
        <div class="subcard-header">
          <p class="header-title">Restricciones financieras</p>
          <b-button
            class="header-icon"
            size="is-small"
            :icon-left="financialRestrictionsOpen ? 'angle-up' : 'angle-down'"
            @click="toggleFinancialRestrictions" />
        </div>

        <div v-if="financialRestrictionsOpen" class="subcard-content fields-has-text-right">
          <!-- Máxima carga financiera -->
          <BInputExpanded
            :disabled="readOnly"
            label="Máxima carga financiera"
            placeholder="Ingresa valor"
            prefix="%"
            size="is-small"
            prefix-position="left"
            :value="formatPercentage(simulation.offerParams.maximumBackendRatio)" />

          <!-- Máximo dividendo-renta -->
          <BInputExpanded
            :disabled="readOnly"
            label="Máximo dividendo-renta"
            placeholder="Ingresa valor"
            prefix="%"
            size="is-small"
            prefix-position="left"
            :value="formatPercentage(simulation.offerParams.maximumFrontendRatio)" />

          <!-- Máximo deuda-garantía -->
          <BInputExpanded
            :disabled="readOnly"
            label="Máximo deuda-garantía"
            placeholder="Ingresa valor"
            prefix="%"
            size="is-small"
            prefix-position="left"
            :value="formatPercentage(simulation.offerParams.maximumCreditLTV)" />

          <!-- Pie mínimo -->
          <BInputExpanded
            :disabled="readOnly"
            label="Pie mínimo"
            placeholder="Ingresa valor"
            prefix="%"
            size="is-small"
            prefix-position="left"
            :value="formatPercentage(1 - simulation.offerParams.maximumPropertyLTV)" />

          <!-- Oferta máxima -->
          <BInputExpanded
            v-model="simulation.offerParams.creditMaxAmount"
            :disabled="readOnly"
            label="Oferta máxima"
            placeholder="Ingresa valor"
            prefix="UF"
            size="is-small"
            prefix-position="left" />

          <!-- Oferta mínima -->
          <BInputExpanded
            v-model="simulation.offerParams.creditMinAmount"
            :disabled="readOnly"
            label="Oferta mínima"
            placeholder="Ingresa valor"
            prefix="UF"
            size="is-small"
            prefix-position="left" />
        </div>
      </div>

      <!-- Estructura del dividendo -->
      <div class="subcard is-gray">
        <div class="subcard-header">
          <p class="header-title">Estructura del dividendo</p>
          <b-button
            class="header-icon"
            size="is-small"
            :icon-left="dividendStructureOpen ? 'angle-up' : 'angle-down'"
            @click="toggleDividendStructure" />
        </div>
        <div v-if="dividendStructureOpen" class="subcard-content fields-has-text-right">
          <!-- Forma de amortización -->
          <b-field label="Forma de amortización" horizontal>
            <b-select
              v-model="simulation.offerParams.amortizationMethod"
              :disabled="readOnly"
              size="is-small"
              placeholder="Selecciona una forma"
              expanded>
              <option value="FRENCH">Francesa</option>
              <option value="GERMANY">Alemana</option>
            </b-select>
          </b-field>

          <!-- Tasa de interés anual -->
          <BInputExpanded
            :disabled="readOnly"
            label="Tasa de interés anual"
            placeholder="Ingresa tasa"
            prefix="%"
            size="is-small"
            :value="formatPercentage(simulation.offerParams.annualInterestRate)"
            prefix-position="left" />

          <!-- Tipo de interés -->
          <b-field label="Tipo de interés" horizontal>
            <b-select
              v-model="simulation.offerParams.interestType"
              :disabled="readOnly"
              size="is-small"
              placeholder="Selecciona un tipo"
              expanded>
              <!-- Opciones del select aquí -->
              <option value="COMPOUND">Compuesto</option>
            </b-select>
          </b-field>

          <!-- Amortización pospuesta -->
          <div class="subcard">
            <b-field label="Amortización pospuesta" horizontal>
              <b-switch v-model="simulation.offerParams.isCreditInsuranceAccelerated" size="is-small" :disabled="readOnly" />
            </b-field>

            <div v-if="simulation.offerParams.isCreditInsuranceAccelerated" class="fields-horizontal">
              <!-- Distribución -->
              <b-field label="Distribución" horizontal>
                <div class="inner">
                  <div class="inner-col">
                    <BInputExpanded
                      v-model="distributionStart"
                      :disabled="readOnly"
                      label="Desde"
                      placeholder="1"
                      prefix="cuota"
                      size="is-small"
                      prefix-position="left" />
                  </div>
                  <div class="inner-col">
                    <BInputExpanded
                      :disabled="readOnly"
                      label="Hasta"
                      placeholder="5"
                      prefix="cuota"
                      size="is-small"
                      prefix-position="left" />
                  </div>
                </div>
              </b-field>
            </div>
          </div>

          <!-- Interés pospuesto -->
          <div class="subcard">
            <b-field label="Interés pospuesto" horizontal>
              <b-switch v-model="graceMonthsInterestMethod" size="is-small" :disabled="readOnly" />
            </b-field>
            <div v-if="graceMonthsInterestMethod">
              <!-- Distribución -->
              <b-field label="Distribución" horizontal>
                <div class="inner">
                  <div class="inner-col">
                    <BInputExpanded
                      :disabled="readOnly"
                      label="Desde"
                      placeholder="1"
                      prefix="cuota"
                      size="is-small"
                      prefix-position="left" />
                  </div>
                  <div class="inner-col">
                    <BInputExpanded
                      :disabled="readOnly"
                      label="Hasta"
                      placeholder="5"
                      prefix="cuota"
                      size="is-small"
                      prefix-position="left" />
                  </div>
                </div>
              </b-field>
            </div>
          </div>
        </div>
      </div>

      <!-- Seguro de crédito -->
      <div class="subcard is-gray">
        <div class="subcard-header header-switch">
          <b-field label="¿Tiene seguro de crédito?" horizontal>
            <b-switch v-model="hasCreditInsurance" :value="simulation.offerParams.creditInsuranceRate !== 0" size="is-small" :disabled="readOnly" />
          </b-field>
        </div>
        <div v-if="creditInsuranceStructureOpen || hasCreditInsurance" class="subcard-content fields-has-text-right">
          <div v-if="readOnly ? simulation.offerParams.creditInsuranceRate !== 0 : hasCreditInsurance">
            <!-- Tasa bruta -->
            <BInputExpanded
              :disabled="readOnly"
              label="Tasa bruta"
              placeholder="Ingresa valor"
              prefix="%"
              size="is-small"
              :value="formatPercentage(grossRate)"
              prefix-position="left" />

            <!-- Tasa líquida -->
            <BInputExpanded
              :disabled="readOnly"
              label="Tasa líquida"
              placeholder="Ingresa valor"
              prefix="%"
              size="is-small"
              :value="formatPercentage(simulation.offerParams.creditInsuranceRate)"
              prefix-position="left" />

            <!-- Tasa de impuesto -->
            <BInputExpanded
              :disabled="readOnly"
              label="Tasa de impuesto"
              placeholder="Ingresa valor"
              prefix="%"
              size="is-small"
              :value="formatPercentage(simulation.offerParams.taxRate)"
              prefix-position="left" />

            <div class="subcard">
              <!-- ¿Se incluye en el crédito? -->
              <b-field label="¿Se incluye en el crédito?" horizontal>
                <b-switch v-model="isIncludedInCredit" :value="simulation.generalParams.insuranceableValue !== 0" size="is-small" :disabled="readOnly" />
              </b-field>
              <div v-if="readOnly ? simulation.generalParams.insuranceableValue !== 0 : isIncludedInCredit">
                <!-- Cantidad de cuotas -->
                <BInputExpanded
                  v-model="simulation.offerParams.creditInsuranceNumberOfMonths"
                  :disabled="readOnly"
                  label="Cantidad de cuotas"
                  placeholder="Ingresa número"
                  prefix="cuotas"
                  size="is-small"
                  prefix-position="left" />

                <!-- Distribución -->
                <b-field label="Distribución" horizontal>
                  <div class="inner">
                    <div class="inner-col">
                      <!-- Desde cuota -->
                      <BInputExpanded
                        v-model="distributionStartCredit"
                        :disabled="readOnly"
                        label="desde"
                        placeholder="1"
                        prefix="cuota"
                        size="is-small"
                        prefix-position="left" />
                    </div>
                    <div class="inner-col">
                      <!-- Hasta cuota -->
                      <BInputExpanded
                        :disabled="readOnly"
                        label="hasta"
                        placeholder="5"
                        prefix="cuota"
                        size="is-small"
                        prefix-position="left" />
                    </div>
                  </div>
                </b-field>

                <!-- ¿Se acelera el pago? -->
                <div class="subcard">
                  <b-field label="¿Se acelera el pago?" horizontal>
                    <b-switch v-model="isPaymentAccelerated" :value="simulation.offerParams.isCreditInsuranceAccelerated" size="is-small" :disabled="readOnly" />
                  </b-field>
                  <div v-if="readOnly ? simulation.offerParams.isCreditInsuranceAccelerated:isPaymentAccelerated">
                    <!-- Distribución -->
                    <b-field label="Distribución" horizontal>
                      <div class="inner">
                        <div class="inner-col">
                          <!-- Desde cuota -->
                          <BInputExpanded
                            v-model="distributionStartCredit"
                            :disabled="readOnly"
                            label="desde"
                            placeholder="1"
                            prefix="cuota"
                            size="is-small"
                            prefix-position="left" />
                        </div>
                        <div class="inner-col">
                          <!-- Hasta cuota -->
                          <BInputExpanded
                            :disabled="readOnly"
                            label="hasta"
                            placeholder="5"
                            prefix="cuota"
                            size="is-small"
                            prefix-position="left" />
                        </div>
                      </div>
                    </b-field>

                    <!-- Tipo de deuda -->
                    <b-field label="Tipo de deuda" horizontal>
                      <b-select
                        v-model="simulation.offerParams.creditInsuranceFeeTermDefinition"
                        :disabled="readOnly"
                        size="is-small"
                        placeholder="Selecciona un tipo de deuda"
                        expanded>
                        <option value="SHORT">Corto plazo</option>
                        <option value="LONG">Largo plazo</option>
                        <!-- Más opciones aquí -->
                      </b-select>
                    </b-field>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Seguros obligatorios -->
      <div class="subcard is-gray">
        <div class="subcard-header">
          <p class="header-title">Seguros obligatorios</p>
          <b-button
            class="header-icon"
            size="is-small"
            :icon-left="insuranceStructureOpen ? 'angle-up' : 'angle-down'"
            @click="toggleInsuranceStructure" />
        </div>
        <div v-if="insuranceStructureOpen" class="subcard-content fields-has-text-right">
          <div class="subcard">
            <!-- Tasa seguro de incendio -->
            <BInputExpanded
              :value="formatPercentage(simulation.offerParams.propertyInsuranceMonthlyRate)"
              :disabled="readOnly"
              label="Tasa seguro de incendio"
              placeholder="Ingresa tasa"
              prefix="%"
              size="is-small"
              prefix-position="left" />

            <!-- Valor asegurable -->
            <BInputExpanded
              :disabled="readOnly"
              label="Valor asegurable"
              placeholder="Ingresa valor"
              prefix="UF"
              size="is-small"
              prefix-position="left" />
          </div>
          <div class="subcard">
            <!-- Tasa seguro de cesantía -->
            <BInputExpanded
              :disabled="readOnly"
              label="Tasa seguro de cesantía"
              placeholder="Ingresa tasa"
              prefix="%"
              size="is-small"
              prefix-position="left"
              :value="formatPercentage(simulation.offerParams.unemploymentInsuranceMonthlyRate)" />

            <!-- Tasa subvención estatal -->
            <BInputExpanded
              :disabled="readOnly"
              label="Tasa subvención estatal"
              placeholder="Ingresa tasa"
              prefix="%"
              size="is-small"
              prefix-position="left"
              :value="formatPercentage(simulation.offerParams.subsidyRate)" />
          </div>
          <div class="subcard">
            <!-- Tipo de seguro de vida -->
            <b-field label="Tipo de seguro de vida" horizontal>
              <b-select
                v-model="simulation.offerParams.lifeInsuranceTypeOfPremium"
                :disabled="readOnly"
                size="is-small"
                placeholder="Selecciona un tipo"
                expanded>
                <option value="MULTIPLE">Multiple</option>
                <!-- Más opciones aquí -->
              </b-select>
            </b-field>
            <b-field label="Deudor" horizontal>
              <div class="inner">
                <!-- Desde año -->
                <div class="inner-col">
                  <BInputExpanded
                    :disabled="readOnly"
                    placeholder="1"
                    prefix="desde"
                    size="is-small"
                    prefix-position="left"
                    :value="getLifeInsuranceDebtor()?.minAge || ''" />
                </div>
                <!-- Hasta año -->
                <div class="inner-col">
                  <BInputExpanded
                    :disabled="readOnly"
                    placeholder="5"
                    prefix="hasta"
                    size="is-small"
                    prefix-position="left"
                    :value="getLifeInsuranceDebtor()?.maxAge || ''" />
                </div>
                <!-- % -->
                <div class="inner-col">
                  <BInputExpanded
                    :disabled="readOnly"
                    placeholder="0,0076"
                    prefix="%"
                    size="is-small"
                    prefix-position="left"
                    :value="formatPercentage(getLifeInsuranceDebtor()?.rate)" />
                </div>
              </div>
            </b-field>
            <!--            <b-field label="Codeudor" horizontal>-->
            <!--              <div class="inner">-->
            <!--                &lt;!&ndash; Desde año &ndash;&gt;-->
            <!--                <div class="inner-col">-->
            <!--                  <b-field label="Desde" horizontal class="has-addons is-small">-->
            <!--                    <div class="field has-addons">-->
            <!--                      <p class="control">-->
            <!--                        <a class="button is-static">año</a>-->
            <!--                      </p>-->
            <!--                      <b-input-->
            <!--                        :value="getLifeInsuranceCodebtor()?.minAge || ''"-->
            <!--                        :disabled="readOnly"-->
            <!--                        type="number"-->
            <!--                        size="is-small"-->
            <!--                        placeholder="1"-->
            <!--                        expanded />-->
            <!--                    </div>-->
            <!--                  </b-field>-->
            <!--                </div>-->
            <!--                &lt;!&ndash; Hasta año &ndash;&gt;-->
            <!--                <div class="inner-col">-->
            <!--                  <b-field label="Hasta" horizontal class="has-addons is-small">-->
            <!--                    <div class="field has-addons">-->
            <!--                      <p class="control">-->
            <!--                        <a class="button is-static">año</a>-->
            <!--                      </p>-->
            <!--                      <b-input-->
            <!--                        :value="getLifeInsuranceCodebtor()?.maxAge || ''"-->
            <!--                        :disabled="readOnly"-->
            <!--                        type="number"-->
            <!--                        size="is-small"-->
            <!--                        placeholder="5"-->
            <!--                        expanded />-->
            <!--                    </div>-->
            <!--                  </b-field>-->
            <!--                </div>-->
            <!--                &lt;!&ndash; % &ndash;&gt;-->
            <!--                <div class="inner-col">-->
            <!--                  <b-field horizontal class="has-addons is-small">-->
            <!--                    <div class="field has-addons">-->
            <!--                      <p class="control">-->
            <!--                        <a class="button is-static">%</a>-->
            <!--                      </p>-->
            <!--                      <b-input-->
            <!--                        :value="formatPercentage(getLifeInsuranceCodebtor()?.rate)"-->
            <!--                        :disabled="readOnly"-->
            <!--                        size="is-small"-->
            <!--                        placeholder="0,0076"-->
            <!--                        expanded />-->
            <!--                    </div>-->
            <!--                  </b-field>-->
            <!--                </div>-->
            <!--              </div>-->
            <!--            </b-field>-->
          </div>
        </div>
      </div>

      <!-- Garantia -->
      <div class="subcard is-gray">
        <div class="subcard-header header-switch">
          <b-field label="¿Tiene garantía?" horizontal>
            <b-switch v-model="hasGuarantee" :value="simulation.offerParams.segment === 'INVESTMENT'" size="is-small" :disabled="readOnly" />
          </b-field>
        </div>
        <div v-if="guaranteeStructureOpen || hasGuarantee" class="subcard-content fields-has-text-right">
          <!-- ¿Tiene garantía? -->
          <div v-if="readOnly ? simulation.offerParams.segment === 'INVESTMENT' : hasGuarantee">
            <!-- Ponderado inversionista -->
            <BInputExpanded
              :disabled="readOnly"
              label="Ponderado inversionista"
              placeholder="Ingresa valor"
              prefix="%"
              size="is-small"
              prefix-position="left"
              :value="formatPercentage(simulation.offerParams.investmentWeight)" />

            <!-- Factor de propiedad -->
            <BInputExpanded
              :disabled="readOnly"
              label="Factor de propiedad"
              placeholder="Ingresa valor"
              prefix="%"
              size="is-small"
              prefix-position="left"
              :value="formatPercentage(simulation.offerParams.propertyTypeFactor)" />
          </div>
        </div>
      </div>

      <!-- Gastos operacionales -->
      <div class="subcard is-gray">
        <div class="subcard-header">
          <p class="header-title">Gastos operacionales</p>
          <b-button
            class="header-icon"
            size="is-small"
            :icon-left="operationalExpensesStructureOpen ? 'angle-up' : 'angle-down'"
            @click="toggleOperationalExpensesStructure" />
        </div>
        <div v-if="operationalExpensesStructureOpen" class="subcard-content fields-has-text-right">
          <!-- Estudio de título -->
          <b-field label="Estudio de título" horizontal class="is-expanded">
            <BInputExpanded
              title="Parámetro"
              :disabled="readOnly"
              placeholder="Valor"
              prefix="UF"
              size="is-small"
              prefix-position="left"
              :value="simulation.offerParams.titleStudyExpenses" />
            <BInputExpanded
              title="Financiamiento"
              :disabled="readOnly"
              placeholder="%"
              prefix="%"
              size="is-small"
              prefix-position="left"
              :value="formatPercentage(simulation.offerParams.titleStudyExpensesPercentage)" />
            <div>
              <div class="field-title">
                <small>Convenios</small>
              </div>
              <b-switch
                title="Convenio"
                :value="getAgreementOfRealState('titleStudyExpenses').active"
                size="is-small"
                :disabled="readOnly" />
            </div>
          </b-field>

          <!-- Tasación -->
          <b-field label="Tasación" horizontal class="is-expanded">
            <BInputExpanded
              :disabled="readOnly"
              placeholder="Valor"
              prefix="UF"
              size="is-small"
              prefix-position="left"
              :value="simulation.offerParams.appraisalExpenses" />
            <BInputExpanded
              :disabled="readOnly"
              placeholder="%"
              prefix="%"
              size="is-small"
              prefix-position="left"
              :value="formatPercentage(simulation.offerParams.appraisalExpensesPercentage)" />
            <b-switch :value="getAgreementOfRealState('appraisalExpenses').active" size="is-small" :disabled="readOnly" />
          </b-field>

          <!-- Escrituración -->
          <b-field label="Escrituración" horizontal class="is-expanded">
            <BInputExpanded
              :disabled="readOnly"
              placeholder="Valor"
              prefix="UF"
              size="is-small"
              prefix-position="left"
              :value="simulation.offerParams.deedExpenses" />
            <BInputExpanded
              :disabled="readOnly"
              placeholder="%"
              prefix="%"
              size="is-small"
              prefix-position="left"
              :value="formatPercentage(simulation.offerParams.deedExpensesPercentage)" />
            <b-switch :value="getAgreementOfRealState('deedExpenses').active" size="is-small" :disabled="readOnly" />
          </b-field>

          <!-- Notaria -->
          <b-field label="Notaria" horizontal class="is-expanded">
            <BInputExpanded
              :disabled="readOnly"
              placeholder="Valor"
              prefix="UF"
              size="is-small"
              prefix-position="left"
              :value="simulation.offerParams.notaryExpenses" />
            <BInputExpanded
              :disabled="readOnly"
              placeholder="%"
              prefix="%"
              size="is-small"
              prefix-position="left"
              :value="formatPercentage(simulation.offerParams.notaryExpensesPercentage)" />
            <b-switch :value="getAgreementOfRealState('notaryExpenses').active" size="is-small" :disabled="readOnly" />
          </b-field>

          <!-- Inscripción -->
          <b-field label="Inscripción" horizontal class="is-expanded">
            <BInputExpanded
              :disabled="readOnly"
              placeholder="Valor"
              prefix="UF"
              size="is-small"
              prefix-position="left"
              :value="simulation.offerParams.realEstateExpensesRate" />
            <BInputExpanded
              :disabled="readOnly"
              placeholder="%"
              prefix="%"
              size="is-small"
              prefix-position="left"
              :value="formatPercentage(simulation.offerParams.realEstateExpensesPercentage)" />
            <b-switch :value="getAgreementOfRealState('realEstateExpensesRate').active" size="is-small" :disabled="readOnly" />
          </b-field>

          <!-- Impuesto al mutuo -->
          <b-field label="Impuesto al mutuo" horizontal class="is-expanded">
            <BInputExpanded
              :disabled="readOnly"
              placeholder="Valor"
              prefix="UF"
              size="is-small"
              prefix-position="left"
              :value="simulation.offerParams.creditTaxRate" />
            <BInputExpanded
              :disabled="readOnly"
              placeholder="%"
              prefix="%"
              size="is-small"
              prefix-position="left"
              :value="formatPercentage(simulation.offerParams.creditTaxRatePercentage)" />
            <b-switch :value="getAgreementOfRealState('creditTaxRate').active" size="is-small" :disabled="readOnly" />
          </b-field>
        </div>
      </div>

      <!-- Elegibilidad -->
      <div v-if="eligibilityData" class="subcard is-gray">
        <div class="subcard-header">
          <p class="header-title">Reglas de Elegibilidad</p>
          <b-button
            class="header-icon"
            size="is-small"
            :icon-left="eligibilityOpen ? 'angle-up' : 'angle-down'"
            @click="toggleEligibility" />
        </div>

        <div v-if="eligibilityOpen" class="subcard-content fields-has-text-right">
          <!-- Tipo -->
          <b-field label="Tipo" horizontal>
            <b-select
              :value="eligibilityType.type"
              :disabled="readOnly"
              size="is-small"
              placeholder="Selecciona un tipo"
              expanded>
              <option value="current">Inmediato</option>
            </b-select>
          </b-field>

          <!-- Edad -->
          <b-field label="Edad" horizontal>
            <b-input
              :value="eligibilityData?.age?.description ?? '--'"
              :disabled="readOnly"
              size="is-small"
              placeholder="Ingresa edad"
              expanded />
          </b-field>

          <!-- Nacionalidad -->
          <b-field label="Nacionalidad" horizontal>
            <b-input
              :value="eligibilityData?.nationality?.description ?? '--'"
              :disabled="readOnly"
              size="is-small"
              placeholder="Ingresa nacionalidad"
              expanded />
          </b-field>

          <!-- Residencia -->
          <b-field label="Residencia" horizontal>
            <b-input
              :value="eligibilityData?.residence?.description ?? '--'"
              :disabled="readOnly"
              size="is-small"
              placeholder="Ingresa residencia"
              expanded />
          </b-field>

          <!-- PEP (Persona Expuesta Políticamente) -->
          <b-field label="PEP" horizontal>
            <b-input
              :value="eligibilityData?.publicExposedPerson?.description ?? '--'"
              :disabled="readOnly"
              size="is-small"
              placeholder="Persona Expuesta Políticamente"
              expanded />
          </b-field>

          <!-- Lista negra externa -->
          <b-field label="Lista negra externa" horizontal>
            <b-input
              :value="eligibilityData?.blackListExternal?.description ?? '--'"
              :disabled="readOnly"
              size="is-small"
              placeholder="Ingresa lista negra externa"
              expanded />
          </b-field>

          <!-- Lista negra interna -->
          <b-field label="Lista negra interna" horizontal>
            <b-input
              :value="eligibilityData?.blackListInternal?.description ?? '--'"
              :disabled="readOnly"
              size="is-small"
              placeholder="Ingresa lista negra interna"
              expanded />
          </b-field>

          <!-- Continuidad laboral -->
          <b-field label="Continuidad laboral" horizontal>
            <b-input
              :value="eligibilityData?.jobContinuity?.description ?? '--'"
              :disabled="readOnly"
              size="is-small"
              placeholder="Ingresa"
              expanded />
          </b-field>

          <!-- Continuidad laboral -->
          <b-field label="Antigüedad laboral" horizontal>
            <b-input
              :value="eligibilityData?.jobSeniority?.description ?? '--'"
              :disabled="readOnly"
              size="is-small"
              placeholder="Ingresa"
              expanded />
          </b-field>

          <!-- Causas judiciales -->
          <b-field label="Causas judiciales" horizontal>
            <b-input
              :value="eligibilityData?.lawsuit?.description ?? '--'"
              :disabled="readOnly"
              size="is-small"
              placeholder="Ingresa causas judiciales"
              expanded />
          </b-field>

          <!-- Nivel de mora -->
          <b-field label="Nivel de mora" horizontal>
            <b-input
              :value="eligibilityData?.defaultRate?.description ?? '--'"
              :disabled="readOnly"
              size="is-small"
              placeholder="Ingresa nivel de mora"
              expanded />
          </b-field>
        </div>
      </div>

      <!-- Valor moneda / Fecha conversion -->
      <div class="subcard">
        <!-- Valor moneda -->
        <BInputExpanded
          v-model="simulation.offerInputs.secondaryCurrencyFactor"
          label="Valor moneda"
          :disabled="readOnly"
          placeholder="Ingresa valor"
          prefix="CLP"
          size="is-small"
          prefix-position="left" />

        <!-- Fecha de conversión -->
        <b-field label="Fecha de conversión" horizontal>
          <div class="field has-addons">
            <p class="control">
              <a class="button is-static">
                <b-icon icon="calendar-days" size="is-small" />
              </a>
            </p>
            <b-input
              :disabled="readOnly"
              size="is-small"
              placeholder="Selecciona una fecha"
              :value="formatDate(simulation.createdAt)"
              expanded />
          </div>
        </b-field>
      </div>
    </div>
  </b-collapse>
</template>

<script>

import { mapState } from 'vuex';
import { formatPercentage, formatDate } from '@/infrastructure/format';
import { EligibilityClient } from '@/application/services/evaluations/apis/eligibility-client';
import { eligibilityBackend } from '@/application/api';
import { authenticationService } from '@/application/services/api-clients/login-service';
import { EligibilityService } from '@/application/services/evaluations/eligibility-service';
import BInputExpanded from '@/ui/components/input/BInputExpanded.vue';

export default {
  name: 'ParamsCard',
  components: { BInputExpanded },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      financialRestrictionsOpen: false,
      dividendStructureOpen: false,
      postponedAmortization: false,
      postponedInterest: false,
      hasCreditInsurance: false,
      isIncludedInCredit: false,
      isPaymentAccelerated: false,
      creditInsuranceStructureOpen: false,
      insuranceStructureOpen: false,
      guaranteeStructureOpen: false,
      hasGuarantee: false,
      eligibilityOpen: false,
      operationalExpensesStructureOpen: false,
      acceleratedPaymentStart: null,
      acceleratedPaymentEnd: null,
      distributionStart: null,
      maxFinancialLoad: null,
      maxDividendIncome: null,
      maxDebtGuarantee: null,
      minimumDownPayment: null,
      maximumOffer: null,
      minimumOffer: null,
      numberOfInstallments: null,
      distributionStartCredit: null,
      distributionEndCredit: null,
      lifeInsuranceType: null,
      grossRate: null,
      netRate: null,
      taxRate: null,
      graceMonthsInterestMethod: false,
      eligibilityData: null,
      eligibilityType: null,
      eligibilityService: new EligibilityService(
        this.$store,
        new EligibilityClient(eligibilityBackend, authenticationService)
      )
    };
  },
  computed: {
    ...mapState('evaluation', ['simulation', 'manualEvaluationId'])
  },
  mounted() {
    this.graceMonthsInterestMethod = this.simulation.offerParams.graceMonthsInterestMethod === 'POSTPONED';
    this.getEligibilityByEvaluationId();
  },
  methods: {
    formatPercentage,
    formatDate,
    toggleFinancialRestrictions() {
      this.financialRestrictionsOpen = !this.financialRestrictionsOpen;
    },
    toggleDividendStructure() {
      this.dividendStructureOpen = !this.dividendStructureOpen;
    },
    toggleInsuranceStructure() {
      this.insuranceStructureOpen = !this.insuranceStructureOpen;
    },
    toggleEligibility() {
      this.eligibilityOpen = !this.eligibilityOpen;
    },
    toggleOperationalExpensesStructure() {
      this.operationalExpensesStructureOpen = !this.operationalExpensesStructureOpen;
    },
    getAgreementOfRealState(type) {
      return this.$getSafe(this, 'simulation.offerInputs.realEstate.agreement', [])
        .find(item => item.type === type) || { active: false };
    },
    getLifeInsuranceDebtor() {
      return {
        minAge: this.$getSafe(this, 'simulation.offerParams.lifeInsuranceDebtorMonthlyRateRanges[0].minAge', ''),
        maxAge: this.$getSafe(this, 'simulation.offerParams.lifeInsuranceDebtorMonthlyRateRanges[0].maxAge', ''),
        rate: this.$getSafe(this, 'simulation.offerParams.lifeInsuranceDebtorMonthlyRateRanges[0].lifeInsuranceMonthlyRate', '')
      };
    },
    getLifeInsuranceCodebtor() {
      return {
        minAge: this.$getSafe(this, 'this.simulation.offerParams.lifeInsuranceDebtorMonthlyRateRanges[1].minAge', ''),
        maxAge: this.$getSafe(this, 'this.simulation.offerParams.lifeInsuranceDebtorMonthlyRateRanges[1].maxAge', ''),
        rate: this.$getSafe(this, 'simulation.offerParams.lifeInsuranceDebtorMonthlyRateRanges[1].lifeInsuranceMonthlyRate', '')
      };
    },
    async getEligibilityByEvaluationId() {
      const { policyCheckResultsOriginal, context } = await this.eligibilityService.getEligibilityPolicyAndContext(this.manualEvaluationId || this.$route.params.evaluationId);
      this.eligibilityData = policyCheckResultsOriginal;
      this.eligibilityType = context;
    }
  }
};
</script>

<style scoped>
.subtitle {
  font-size: 0.75rem;
}

</style>
